import { useState } from 'react'

import EyeInvisibleOutlinedIcon from '@ant-design/icons/EyeInvisibleOutlined'
import EyeOutlinedIcon from '@ant-design/icons/EyeOutlined'
import LockOutlinedIcon from '@ant-design/icons/LockOutlined'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import Fade from '@mui/material/Fade'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'

import InputControl from '@components/InputControl'

import { useForm, withBreakGlassCheck } from './BreakGlass.hooks'

const BreakGlassInDialogComp = (props) => (
  <Fade in>
    <DialogContent sx={styles.dialog}>
      <BreakGlass {...props} />
    </DialogContent>
  </Fade>
)

function BreakGlassPaperComp(props) {
  return (
    <Fade in>
      <Paper sx={styles.paper}>
        <BreakGlass {...props} />
      </Paper>
    </Fade>
  )
}

export const BreakGlassInDialog = withBreakGlassCheck(BreakGlassInDialogComp)
export const BreakGlassPaper = withBreakGlassCheck(BreakGlassPaperComp)

function BreakGlass({ patientId, onSuccess, onClose, reasonRequired = false }) {
  const formik = useForm({ patientId, onSuccess, reasonRequired })

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Stack spacing={2} sx={styles.content}>
        <Typography variant="h2">
          Warning!
          <br />
          Confidential information.
        </Typography>
        <Typography variant="h4">You must enter your password to continue and your access will be recorded.</Typography>
        <PasswordField formik={formik} />
        {reasonRequired && <ReasonField formik={formik} />}
        <Stack direction="row" spacing={2}>
          {onClose && (
            <Button
              fullWidth
              size="large"
              color="error"
              onClick={() => {
                formik.resetForm()
                onClose()
              }}
              data-testid="break-glass-cancel-button"
            >
              Cancel
            </Button>
          )}
          <Button
            fullWidth
            size="large"
            type="submit"
            loading={formik.isSubmitting}
            variant="contained"
            endIcon={<LockOutlinedIcon />}
            data-testid="break-glass-unlock-button"
          >
            Unlock
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}

function ReasonField({ formik }) {
  const reasons = useLookup(Lookup.BreakGlassReasons)

  return (
    <InputControl field="reason" formikProps={formik}>
      <TextField required fullWidth select label="Reason" data-testid="break-glass-reason-select">
        {reasons.map((reason) => (
          <MenuItem key={reason} value={reason}>
            {reason}
          </MenuItem>
        ))}
      </TextField>
    </InputControl>
  )
}

function PasswordField({ formik }) {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <InputControl field="password" formikProps={formik}>
      <TextField
        autoFocus
        required
        fullWidth
        type={showPassword ? 'text' : 'password'}
        placeholder="Enter your password"
        slotProps={{
          htmlInput: { style: { textAlign: 'center' } },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((sp) => !sp)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  color="secondary"
                  data-testid="show-password-btn"
                  data-test-visible={showPassword}
                >
                  {showPassword ? <EyeOutlinedIcon /> : <EyeInvisibleOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    </InputControl>
  )
}

const styles = {
  paper: {
    textAlign: 'center',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 2,
    flex: '1 1 auto',
  },
  dialog: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 500,
    textAlign: 'center',
  },
  content: {
    maxWidth: 420,
  },
}
